import type { PropsWithChildren } from 'react';

import { ApolloClient, ApolloLink, HttpLink, InMemoryCache } from '@apollo/client';
import { loadDevMessages, loadErrorMessages } from '@apollo/client/dev';
import { ApolloProvider } from '@apollo/client/react';
import { getMainDefinition } from '@apollo/client/utilities';
import { Navigation } from '@drycut/navigation';
import { createConsumer } from '@rails/actioncable';
import ActionCableLink from 'graphql-ruby-client/subscriptions/ActionCableLink';
import { getCookie } from 'typescript-cookie';

type Environment = 'development' | 'production' | 'test';
const env = import.meta.env.MODE as Environment;

if (env === 'development' || env === 'test') {
  // Adds messages only in a dev environment
  loadDevMessages();
  loadErrorMessages();
}

const isSubscription = ({ query }: ApolloLink.Operation) => {
  const definition = getMainDefinition(query);

  return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
};

// Append an authorization header to the request for authenticated requests
const authMiddleware = new ApolloLink((operation, forward) => {
  const token = sessionStorage.getItem('token');

  operation.setContext({
    headers: {
      authorization: token ? `Bearer ${token}` : '',
    },
  });

  return forward(operation);
});

// Splits the connection into two links. If hasSubscriptionOperation returns true then the first
// link (new ActionCableLink) is used, otherwise the second link (new HttpLink) is used.
//
// For more details check:
// https://www.apollographql.com/docs/react/api/link/introduction/#directional-composition
const gqlLink = ApolloLink.split(
  isSubscription,
  new ActionCableLink({
    cable: createConsumer(
      `${Navigation.GRAPH_API_WS_URI}?token=${getCookie('__DRYCUT_info_token')}`,
    ),
  }),
  new HttpLink({ uri: Navigation.GRAPH_API_URL }),
);

// To avoid caching and clients with the wrong API token we will require a new client each time
// we use the DrycutApolloProvider.
export const initApolloClient = () => {
  const apolloClient = new ApolloClient({
    link: ApolloLink.from([authMiddleware, gqlLink]),
    cache: new InMemoryCache(),
    devtools: {
      enabled: env === 'development',
    },
  });

  return apolloClient;
};

export function DrycutApolloProvider({ children }: PropsWithChildren): React.ReactElement {
  const client = initApolloClient();

  return <ApolloProvider client={client}>{children}</ApolloProvider>;
}
